/**
 * Mock plugin catalogue and sample ECA model.
 * Structure mirrors what the backend API will return — swapping for real calls later
 * requires only replacing the import source.
 */

// ---------------------------------------------------------------------------
// Plugin catalogue
// ---------------------------------------------------------------------------

export const MOCK_PLUGINS = {
  events: [
    {
      id: 'content_entity:presave',
      label: 'Content: Before save',
      description: 'Se déclenche juste avant qu\'une entité est sauvegardée. Produit les tokens [entity] et [account].',
      category: 'Content',
      configSchema: [
        { key: 'type', label: 'Entity type / bundle', type: 'text', placeholder: 'node article', wirable: false },
      ],
      tokenOutputs: ['entity', 'account'],
    },
    {
      id: 'content_entity:insert',
      label: 'Content: After create',
      description: 'Se déclenche après la création d\'une nouvelle entité. Produit les tokens [entity] et [account].',
      category: 'Content',
      configSchema: [
        { key: 'type', label: 'Entity type / bundle', type: 'text', placeholder: 'node article', wirable: false },
      ],
      tokenOutputs: ['entity', 'account'],
    },
    {
      id: 'content_entity:update',
      label: 'Content: After update',
      description: 'Se déclenche après la mise à jour d\'une entité existante. Produit les tokens [entity] et [account].',
      category: 'Content',
      configSchema: [
        { key: 'type', label: 'Entity type / bundle', type: 'text', placeholder: 'node article', wirable: false },
      ],
      tokenOutputs: ['entity', 'account'],
    },
    {
      // eca_base module — BaseEvent deriver key: "eca_cron"
      id: 'eca_cron',
      label: 'Scheduler: Cron trigger',
      description: 'Déclenche le workflow lors de l\'exécution du cron Drupal, selon une expression cron.',
      category: 'System',
      configSchema: [
        {
          key: 'frequency',
          label: 'Cron expression',
          type: 'text',
          placeholder: '0 * * * *',
          wirable: false,
        },
      ],
      tokenOutputs: [],
    },
  ],

  conditions: [
    {
      id: 'eca_entity_field_value',
      label: 'Entity: Field value',
      description: 'Vérifie si un champ de l\'entité courante correspond à une valeur attendue (=, !=, >, <).',
      category: 'Entity',
      configSchema: [
        { key: 'field_name', label: 'Field name', type: 'text', placeholder: 'status', wirable: true },
        { key: 'value', label: 'Expected value', type: 'text', placeholder: '1', wirable: true },
        {
          key: 'operator',
          label: 'Operator',
          type: 'select',
          options: [
            { value: '=', label: '= (equals)' },
            { value: '!=', label: '!= (not equals)' },
            { value: '>', label: '> (greater than)' },
            { value: '<', label: '< (less than)' },
          ],
          wirable: false,
        },
        { key: 'negate', label: 'Negate', type: 'boolean', wirable: false },
      ],
    },
    {
      id: 'eca_entity_type_bundle',
      label: 'Entity: Type / Bundle',
      description: 'Vérifie si l\'entité courante est d\'un type et d\'un bundle donnés (ex. node article).',
      category: 'Entity',
      configSchema: [
        { key: 'type', label: 'Entity type / bundle', type: 'text', placeholder: 'node article', wirable: false },
        { key: 'negate', label: 'Negate', type: 'boolean', wirable: false },
      ],
    },
    {
      // eca_base module — checks whether a token key exists in the context
      id: 'eca_token_exists',
      label: 'Token: Exists',
      description: 'Vérifie si un token est défini dans le contexte courant du workflow.',
      category: 'Token',
      configSchema: [
        { key: 'token_name', label: 'Token key', type: 'text', placeholder: 'response', wirable: true },
        { key: 'negate', label: 'Negate', type: 'boolean', wirable: false },
      ],
    },
  ],

  actions: [
    {
      id: 'action_message_action',
      label: 'System: Display message',
      description: 'Affiche un message flash Drupal (status, warning ou error) à l\'utilisateur courant.',
      category: 'System',
      configSchema: [
        { key: 'message', label: 'Message', type: 'text', placeholder: 'Node published: [node:title]', wirable: true },
        {
          key: 'type',
          label: 'Type',
          type: 'select',
          options: [
            { value: 'status', label: 'Status' },
            { value: 'warning', label: 'Warning' },
            { value: 'error', label: 'Error' },
          ],
          wirable: false,
        },
      ],
      tokenOutputs: [],
    },
    {
      id: 'eca_set_field_value',
      label: 'Entity: Set field value',
      description: 'Modifie la valeur d\'un champ de l\'entité courante. Produit le token [entity] mis à jour.',
      category: 'Entity',
      configSchema: [
        { key: 'field_name', label: 'Field', type: 'text', placeholder: 'field_status', wirable: true },
        { key: 'value', label: 'Value', type: 'text', placeholder: '1', wirable: true },
        { key: 'clear', label: 'Clear first', type: 'boolean', wirable: false },
      ],
      tokenOutputs: ['entity'],
    },
    {
      // eca_base module — stores an arbitrary value into the token service
      id: 'eca_token_set_value',
      label: 'Token: Set value',
      description: 'Stocke une valeur arbitraire dans un token nommé, accessible dans les nodes suivants.',
      category: 'Token',
      configSchema: [
        { key: 'token_name', label: 'Token key', type: 'text', placeholder: 'my_token', wirable: false },
        { key: 'token_value', label: 'Value', type: 'text', placeholder: '[entity:title]', wirable: true },
      ],
      tokenOutputs: [],
    },
    {
      // Custom orbit module eca_rest — derived plugin: eca_rest_collection_export:<collection_id>
      // Each configured REST collection becomes its own derived plugin ID.
      id: 'eca_rest_collection_export',
      label: 'REST: Call collection export',
      description: 'Exécute une requête REST configurée via une Collection Export et stocke la réponse dans un token.',
      category: 'REST',
      configSchema: [
        { key: 'collection', label: 'Collection ID', type: 'text', placeholder: 'my_collection', wirable: false },
        { key: 'token_name', label: 'Store response as', type: 'text', placeholder: 'rest_response', wirable: false },
      ],
      tokenOutputs: ['rest_response'],
    },
    {
      // Drupal core — system module
      id: 'action_send_email_action',
      label: 'Mail: Send email',
      description: 'Envoie un e-mail à un destinataire. Le sujet et le corps acceptent des tokens Drupal.',
      category: 'Mail',
      configSchema: [
        { key: 'recipient', label: 'To', type: 'text', placeholder: '[entity:author:mail]', wirable: true },
        { key: 'subject', label: 'Subject', type: 'text', placeholder: 'Update: [entity:title]', wirable: true },
        { key: 'message', label: 'Body', type: 'text', placeholder: 'Hello [entity:author:name]', wirable: true },
      ],
      tokenOutputs: [],
    },
    {
      // eca_content module — loads an entity into the token context by type + ID
      id: 'eca_token_load_entity',
      label: 'Entity: Load by ID',
      description: 'Charge une entité Drupal par son ID et l\'expose via un token pour les nodes suivants.',
      category: 'Entity',
      configSchema: [
        {
          key: 'entity_type',
          label: 'Entity type',
          type: 'select',
          options: [
            { value: 'node', label: 'Node (node)' },
            { value: 'user', label: 'User (user)' },
            { value: 'taxonomy_term', label: 'Taxonomy term' },
            { value: 'media', label: 'Media' },
            { value: 'paragraph', label: 'Paragraph' },
          ],
          wirable: false,
        },
        {
          key: 'entity_id',
          label: 'Entity ID',
          type: 'text',
          placeholder: '[node:nid] or 42',
          wirable: true,
        },
        {
          key: 'token_name',
          label: 'Store as token',
          type: 'text',
          placeholder: 'entity (default)',
          wirable: false,
        },
      ],
      // Writes the loaded entity into [entity] (or [token_name] if overridden)
      tokenOutputs: ['entity'],
    },
    {
      // eca_views module — executes a View query and stores results as a token list
      // Results accessible as [token_name:0], [token_name:1], etc. (full entities)
      id: 'eca_views_query',
      label: 'Views: Execute query',
      description: 'Exécute une Vue Drupal et stocke les résultats sous forme de liste de tokens indexés.',
      category: 'Entity',
      configSchema: [
        { key: 'view_id', label: 'View ID', type: 'text', placeholder: 'my_view', wirable: false },
        { key: 'display_id', label: 'Display ID', type: 'text', placeholder: 'default', wirable: false },
        {
          key: 'arguments',
          label: 'Arguments',
          type: 'text',
          placeholder: '[entity:nid]',
          wirable: true,
        },
        { key: 'token_name', label: 'Store list as token', type: 'text', placeholder: 'view_results', wirable: false },
      ],
      tokenOutputs: ['view_results'],
    },
  ],

  gateways: [
    {
      id: 'wait_for_all',
      label: 'Wait for all',
      description: 'Join AND : attend que toutes les branches entrantes soient terminées avant de continuer.',
      class: 'flow',
      configSchema: [
        { key: 'reset_after_fire', label: 'Reset after fire', type: 'boolean', wirable: false },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Sample ECA model
// ---------------------------------------------------------------------------
// Scenario: When a node article is saved and is published (status=1),
// notify the admin AND email the author in parallel, then wait for both
// to complete before logging the workflow result.

export const MOCK_MODEL = {
  id: 'sample_publish_workflow',
  label: 'Node publish workflow (demo)',

  events: {
    Event_001: {
      plugin: 'content_entity:presave',
      label: 'Node: Before save',
      configuration: { type: 'node article' },
      successors: [{ id: 'Cond_001', condition: '' }],
      _position: { x: 60, y: 300 },
    },
  },

  conditions: {
    Cond_001: {
      plugin: 'eca_entity_field_value',
      label: 'Is published?',
      configuration: { field_name: 'status', value: '1', operator: '=' },
      successors: [
        { id: 'Action_Notify', condition: '' },
        { id: 'Action_Mail', condition: '' },
      ],
      false_successors: [],
      _position: { x: 360, y: 300 },
    },
  },

  gateways: {
    Gateway_001: {
      plugin: 'wait_for_all',
      label: 'Wait: notify + mail',
      configuration: { reset_after_fire: false },
      successors: [{ id: 'Action_Log', condition: '' }],
      branches: ['Action_Notify', 'Action_Mail'],
      _position: { x: 860, y: 300 },
    },
  },

  actions: {
    Action_Notify: {
      plugin: 'action_message_action',
      label: 'Notify admin',
      // message references the variable _var_msg001 — fromEca() reconstructs the data edge
      configuration: { message: '[_var_msg001]', type: 'status' },
      successors: [{ id: 'Gateway_001', condition: '' }],
      _position: { x: 660, y: 130 },
    },
    Action_Mail: {
      plugin: 'action_send_email_action',
      label: 'Email author',
      configuration: {
        recipient: '[entity:author:mail]',
        subject: 'Your article is live',
        message: 'Hello [entity:author:name], your article "[entity:title]" has been published.',
      },
      successors: [{ id: 'Gateway_001', condition: '' }],
      _position: { x: 660, y: 470 },
    },
    Action_Log: {
      plugin: 'eca_token_set_value',
      label: 'Log completion',
      configuration: { token_name: 'publish_done', token_value: '1' },
      successors: [],
      _position: { x: 1100, y: 300 },
    },
  },

  // ---------------------------------------------------------------------------
  // Editor metadata — stored in third_party_settings, transparent to ECA
  // ---------------------------------------------------------------------------
  third_party_settings: {
    eca_react_editor: {
      variables: {
        // Variable with a default value — floating, not connected to any node
        '_var_demo01': {
          label: 'Demo variable',
          color: '#8B5CF6',
          defaultValue: 'Hello world',
          references: [
            { ref_id: 'ref_0', position: { x: 450, y: 80 } },
            { ref_id: 'ref_1', position: { x: 750, y: 80 } },
          ],
        },
        // Variable connected to Action_Notify's message field
        '_var_msg001': {
          label: 'Notify message',
          color: '#EC4899',
          defaultValue: 'Node published: [entity:title]',
          references: [
            { ref_id: 'ref_0', position: { x: 490, y: 180 } },
          ],
        },
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Helper: look up a plugin definition by category and plugin id
// ---------------------------------------------------------------------------

export const getPluginDef = (category, pluginId) => {
  const cat = MOCK_PLUGINS[category];
  return cat?.find((p) => p.id === pluginId) ?? null;
};

// ---------------------------------------------------------------------------

